import { Dayjs } from "dayjs"
import { useSearchParams } from "react-router-dom"
import { IDashboardPeriodParams } from "ts/interfaces/AdminDashboard"
import { DashboardPeriodPreset, DashboardTab } from "ts/types/AdminDashboard"
import dayjs from "utils/dayjs"

export const DASHBOARD_PRESETS: Exclude<DashboardPeriodPreset, "custom">[] = [
  "1m",
  "3m",
  "6m",
  "school_year",
]

// Namespaced per tab so each tab remembers its own period while switching.
const PARAM_PREFIX: Record<DashboardTab, string> = {
  overview: "ov",
  teachers: "te",
  students: "st",
}

const DATE_FORMAT = "YYYY-MM-DD"
const MAX_RANGE_MONTHS = 24

export type CustomRangeError =
  | "missingDate"
  | "fromAfterTo"
  | "futureDate"
  | "rangeTooLong"

// Guards what BED 500s on (reversed or missing dates) and 400s on (> 24 months).
export const validateCustomRange = (
  from: Dayjs | null,
  to: Dayjs | null,
): CustomRangeError | null => {
  if (!from || !to || !from.isValid() || !to.isValid()) return "missingDate"
  if (from.isAfter(to, "day")) return "fromAfterTo"
  if (to.isAfter(dayjs(), "day")) return "futureDate"
  if (to.diff(from, "month", true) > MAX_RANGE_MONTHS) return "rangeTooLong"
  return null
}

const parseDate = (raw: string | null) =>
  raw ? dayjs(raw, DATE_FORMAT, true) : null

const useDashboardPeriod = (tab: DashboardTab) => {
  const [searchParams, setSearchParams] = useSearchParams()
  const prefix = PARAM_PREFIX[tab]
  const presetKey = `${prefix}_preset`
  const fromKey = `${prefix}_from`
  const toKey = `${prefix}_to`
  // A new period starts the tab's table from its first page.
  const pageKey = `${prefix}_page`
  const rawPreset = searchParams.get(presetKey)
  const rawFrom = searchParams.get(fromKey)
  const rawTo = searchParams.get(toKey)

  const isValidCustom =
    rawPreset === "custom" &&
    !validateCustomRange(parseDate(rawFrom), parseDate(rawTo))
  const params: IDashboardPeriodParams =
    isValidCustom && rawFrom && rawTo
      ? { preset: "custom", date_from: rawFrom, date_to: rawTo }
      : {
          preset:
            DASHBOARD_PRESETS.find((known) => known === rawPreset) ?? "1m",
        }

  // react-router 6.3 has no functional setSearchParams, so writes merge into the
  // current params; a filter change replaces the history entry instead of pushing.
  const write = (patch: Record<string, string | null>) => {
    const next = new URLSearchParams(searchParams)
    Object.entries(patch).forEach(([key, value]) =>
      value === null ? next.delete(key) : next.set(key, value),
    )
    setSearchParams(next, { replace: true })
  }

  const setPreset = (next: Exclude<DashboardPeriodPreset, "custom">) =>
    write({
      [presetKey]: next === "1m" ? null : next,
      [fromKey]: null,
      [toKey]: null,
      [pageKey]: null,
    })

  const setCustomRange = (from: Dayjs, to: Dayjs) =>
    write({
      [presetKey]: "custom",
      [fromKey]: from.format(DATE_FORMAT),
      [toKey]: to.format(DATE_FORMAT),
      [pageKey]: null,
    })

  return { params, setPreset, setCustomRange }
}

export default useDashboardPeriod
